import { HSText } from '@/Components/Common/HSText';
import DoorClosedIcon from '@/Components/Common/Icons/door-closed-icon.svg';
import DoorOpenIcon from '@/Components/Common/Icons/door-open-icon.svg';
import FlushActiveIcon from '@/Components/Common/Icons/FlushActiveIcon';
import FlushInactiveIcon from '@/Components/Common/Icons/FlushInactiveIcon';
import DryIcon from '@/Components/Common/Icons/dry.svg';
import OccupancySensorOccupiedIcon from '@/Components/Common/Icons/occupancy-sensor-occupied.svg';
import OccupancySensorVacantIcon from '@/Components/Common/Icons/occupancy-sensor-vacant.svg';
import PowerIcon from '@/Components/Common/Icons/power.svg';
import WetIcon from '@/Components/Common/Icons/wet.svg';
import CookingIcon from '@/Components/Common/Icons/cooking.svg';
import CookingDoneIcon from '@/Components/Common/Icons/cooking-done.svg';
import {
  COOKTOP_ALERTS_TAB,
  DEVICE_EVENTS_PROPERTY,
  DEVICE_STATES,
  DEVICE_TYPES,
  EVENT_TYPES,
  INCONTINENCE_SENSOR_STATES,
  getRadarOccupancyState,
  RADAR_PRESENCE_TYPES,
  SENSOR_STATE_TITLE_MAPPING,
  SENSORS,
  SMART_PLUG_POWER_CONSUMPTION_THRESHOLD,
} from '@/constants/device';
import { getDayLabelOrDate } from '@/utilities/helpers/commonHelper';
import { getFlushSensorActive } from '@/Components/Sensors/SensorUtils';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getTimeFormat, getTimeZoneFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { Tabs } from 'antd';
import { isEqual, isNil, toLower } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { convertTemperature } from '../../Environmental/EnvironmentalUtils';

const CELSIUS = 'C';
const FAHRENHEIT = 'F';

const COOKTOP_EVENT_TYPES = [
  EVENT_TYPES.COOKTOP_SENSOR.EXPOSED_BURNER,
  EVENT_TYPES.COOKTOP_SENSOR.PRESENCE,
  EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING,
  EVENT_TYPES.COOKTOP_SENSOR.POT_TOO_HOT,
  EVENT_TYPES.COOKTOP_SENSOR.UNATTENDED,
  COOKTOP_ALERTS_TAB,
];

const INITIAL_VISIBLE_EVENTS = 80;
const LOAD_MORE_EVENTS = 40;
const SCROLL_LOAD_THRESHOLD = 80;

const getLastObservation = (obsStr) => {
  if (!obsStr) return null;
  const arr = obsStr.split(',');
  return arr.length ? parseFloat(arr[arr.length - 1]) : null;
};

const SensorState = ({ data, eventType, userTempUnit, dataTempUnit, trans }) => {
  if (eventType === EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_UPDATE_DOOR_WINDOW_SENSOR_OPEN) {
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        {data === 'Closed' ? (
          <DoorClosedIcon className={`sensor-state ${toLower(data)}`} />
        ) : data === 'Open' ? (
          <DoorOpenIcon className={`sensor-state ${toLower(data)}`} />
        ) : (
          '-'
        )}
        <HSText className={`sensor-state-title ${toLower(data)}`}>{data}</HSText>
      </div>
    );
  }
  if (eventType === EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_UPDATE_SMART_PLUG_ON) {
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        <PowerIcon className="power-icon on" />
        <HSText className={`sensor-state-title ${toLower(data)}`}>{data}</HSText>
      </div>
    );
  }
  if (eventType === EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_LAN_SMART_PLUG) {
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        <HSText className="sensor-state-title on">{data}</HSText>
      </div>
    );
  }
  if (eventType === EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_OCCUPANCY_SENSOR_ACTIVE) {
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        {data === 'Occupied' ? (
          <OccupancySensorOccupiedIcon className={`sensor-state ${toLower(data)}`} />
        ) : data === 'Vacant' ? (
          <OccupancySensorVacantIcon className={`sensor-state ${toLower(data)}`} />
        ) : (
          '-'
        )}
        <HSText className="sensor-state-title on">{data}</HSText>
      </div>
    );
  }
  if (eventType === EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_UPDATE_OCCUPANCY_SENSOR_WET) {
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        {data === INCONTINENCE_SENSOR_STATES.DRY ? (
          <DryIcon className={`sensor-state ${toLower(data)}`} />
        ) : data === INCONTINENCE_SENSOR_STATES.WET ? (
          <WetIcon className={`sensor-state ${toLower(data)}`} />
        ) : (
          '-'
        )}
        <HSText className="sensor-state-title on">
          {data && data !== '-' ? trans(String(data).toUpperCase()) : '-'}
        </HSText>
      </div>
    );
  }
  if (eventType === EVENT_TYPES.RADAR_SENSOR.RADAR_PRESENCE_EVENT) {
    const occupancyState = getRadarOccupancyState(data);
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        {occupancyState === DEVICE_STATES.OCCUPANCY_SENSOR.OCCUPIED ? (
          <OccupancySensorOccupiedIcon className="sensor-state occupied" />
        ) : occupancyState === DEVICE_STATES.OCCUPANCY_SENSOR.VACANT ? (
          <OccupancySensorVacantIcon className="sensor-state vacant" />
        ) : (
          '-'
        )}
        <HSText className="sensor-state-title on">
          {data && data !== '-' ? trans(String(data).toUpperCase()) : '-'}
        </HSText>
      </div>
    );
  }
  if (
    eventType === EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR ||
    eventType === EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT
  ) {
    const normalizedDataTempUnit =
      isEqual(toLower(dataTempUnit), 'celsius') || String(dataTempUnit).toUpperCase() === CELSIUS
        ? CELSIUS
        : FAHRENHEIT;

    const temp = convertTemperature(data, normalizedDataTempUnit, userTempUnit);
    let text = '-';
    if (temp !== '-' && !isNaN(temp)) {
      text = `${temp}° ${userTempUnit ?? FAHRENHEIT}`;
    }
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        <HSText className="sensor-state-title on">{text}</HSText>
      </div>
    );
  }
  if (
    eventType === DEVICE_EVENTS_PROPERTY.humidity ||
    eventType === EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_HUMIDITY_REPORT
  ) {
    let text = '-';
    if (data !== '-') {
      text = `${data.toFixed(1)} %`;
    }
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        <HSText className="sensor-state-title on">{text}</HSText>
      </div>
    );
  }
  if (
    eventType === EVENT_TYPES.FLUSH_SENSOR.ACCESSORY_STATE_UPDATE_TOILET_FLUSH_SENSOR_ACTIVE ||
    eventType === EVENT_TYPES.FLUSH_SENSOR.ACCESSORY_STATE_BLE_TOILET_FLUSH_SENSOR
  ) {
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        {data === DEVICE_STATES.FLUSH_SENSOR.ACTIVE ? (
          <FlushActiveIcon className={`sensor-state ${toLower(data)}`} />
        ) : data === DEVICE_STATES.FLUSH_SENSOR.INACTIVE ? (
          <FlushInactiveIcon className={`sensor-state ${toLower(data)}`} />
        ) : (
          '-'
        )}
        <HSText className="sensor-state-title on">{data}</HSText>
      </div>
    );
  }
  if (eventType === EVENT_TYPES.COOKTOP_SENSOR.PRESENCE) {
    const isActive = data === DEVICE_STATES.COOKTOP_SENSOR.ACTIVE;
    const isKnown =
      data === DEVICE_STATES.COOKTOP_SENSOR.ACTIVE ||
      data === DEVICE_STATES.COOKTOP_SENSOR.INACTIVE;
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        {isActive ? (
          <OccupancySensorOccupiedIcon className="sensor-state occupied" />
        ) : isKnown ? (
          <OccupancySensorVacantIcon className="sensor-state vacant" />
        ) : null}
        <HSText className="sensor-state-title on">
          {isKnown ? (isActive ? trans('OCCUPIED') : trans('VACANT')) : '-'}
        </HSText>
      </div>
    );
  }
  if (eventType === EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING) {
    const isActive = data === DEVICE_STATES.COOKTOP_SENSOR.ACTIVE;
    const isKnown =
      data === DEVICE_STATES.COOKTOP_SENSOR.ACTIVE ||
      data === DEVICE_STATES.COOKTOP_SENSOR.INACTIVE;
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        {isActive ? (
          <CookingIcon className="sensor-state cooking-active" />
        ) : isKnown ? (
          <CookingDoneIcon className="sensor-state cooking-inactive" />
        ) : null}
        <HSText className="sensor-state-title on">
          {isKnown ? trans(String(data).toUpperCase()) : '-'}
        </HSText>
      </div>
    );
  }
  if (eventType === COOKTOP_ALERTS_TAB || COOKTOP_EVENT_TYPES.includes(eventType)) {
    const isAlertActive = data === DEVICE_STATES.COOKTOP_SENSOR.ACTIVE;
    let alertLabel = isAlertActive ? trans('ACTIVE') : trans('INACTIVE');
    if (isAlertActive) {
      if (eventType === EVENT_TYPES.COOKTOP_SENSOR.EXPOSED_BURNER) {
        alertLabel = trans('EXPOSED_BURNER');
      } else if (eventType === EVENT_TYPES.COOKTOP_SENSOR.POT_TOO_HOT) {
        alertLabel = trans('POT_TOO_HOT');
      } else if (eventType === EVENT_TYPES.COOKTOP_SENSOR.UNATTENDED) {
        alertLabel = trans('UNATTENDED_COOKING');
      }
    }
    return (
      <div className="modal-wellness-item-content-statistic-info-value">
        <HSText className={`sensor-state-title ${isAlertActive ? 'alert-active' : 'on'}`}>
          {alertLabel}
        </HSText>
      </div>
    );
  }
  return (
    <div className="modal-wellness-item-content-statistic-info-value">
      <HSText className="sensor-state-title on">{data}</HSText>
    </div>
  );
};

SensorState.propTypes = {
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  eventType: PropTypes.string,
  userTempUnit: PropTypes.string,
  dataTempUnit: PropTypes.string,
  trans: PropTypes.func,
};

const Table = ({
  userEvents,
  timezone,
  loginUserLocale,
  userTempUnit,
  sensorDetails,
  eventType,
  loginUserTimeformat,
}) => {
  const [visibleEventCount, setVisibleEventCount] = useState(INITIAL_VISIBLE_EVENTS);
  const historyContainerRef = useRef(null);
  const trans = useSelector(translator);

  const historyEvents = useMemo(() => {
    if (userEvents?.length > 0) {
      if (
        userEvents[0]?.message_type ===
        EVENT_TYPES.SMART_PLUG.ACCESSORY_REPORT_SMART_PLUG_RECENT_ENERGY
      ) {
        //we get the readings in batch for every 15min. each data contains readings as array of 1min data.
        let totalEvents = [];

        try {
          for (const event of userEvents) {
            const oneGroup = JSON.parse(event.readings);
            totalEvents = totalEvents.concat(oneGroup);
          }
        } catch (error) {
          console.error('Error parsing smart plug readings:', error);
        }
        return totalEvents
          .map((e) => {
            return {
              utc_timestamp_inms: e.timestamp,
              value: e.value,
            };
          })
          .sort((a, b) => b.utc_timestamp_inms - a.utc_timestamp_inms);
      }

      return userEvents;
    }
    return [];
  }, [userEvents]);

  const handleHistoryScroll = () => {
    const container = historyContainerRef.current;
    if (!container) return;

    const remainingScroll = container.scrollHeight - container.scrollTop - container.clientHeight;

    if (remainingScroll <= SCROLL_LOAD_THRESHOLD && visibleEventCount < historyEvents.length) {
      setVisibleEventCount((currentCount) =>
        Math.min(currentCount + LOAD_MORE_EVENTS, historyEvents.length)
      );
    }
  };

  const getDataTempUnit = (eventData) => {
    if (eventData?.temperature_unit) {
      return eventData.temperature_unit;
    }
    return null;
  };
  const getSensorState = (eventData, sensor) => {
    if (sensor.devicetype === DEVICE_TYPES.DOOR_WINDOW_SENSOR) {
      const stateProperty = DEVICE_EVENTS_PROPERTY[eventType];
      if (eventData && !isNil(eventData[stateProperty])) {
        return eventData[stateProperty] === true
          ? 'Open'
          : eventData[stateProperty] === false
            ? 'Closed'
            : '-';
      }
    }
    if (sensor.devicetype === DEVICE_TYPES.SMART_PLUG) {
      const stateProperty = DEVICE_EVENTS_PROPERTY[eventType];
      if (eventData && !isNil(eventData[stateProperty])) {
        if (eventType === EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_UPDATE_SMART_PLUG_ON) {
          return eventData[stateProperty] === true
            ? 'On'
            : eventData[stateProperty] === false
              ? 'Off'
              : '-';
        } else if (eventType === EVENT_TYPES.SMART_PLUG.ACCESSORY_REPORT_SMART_PLUG_RECENT_ENERGY) {
          return eventData[stateProperty] > SMART_PLUG_POWER_CONSUMPTION_THRESHOLD
            ? trans('ACTIVE')
            : trans('INACTIVE');
        }
      }
    }
    if (sensor.devicetype === DEVICE_TYPES.OCCUPANCY_SENSOR) {
      const stateProperty = DEVICE_EVENTS_PROPERTY[eventType];
      if (eventData && !isNil(eventData[stateProperty])) {
        if (eventType === EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_OCCUPANCY_SENSOR_ACTIVE) {
          return eventData[stateProperty] === true
            ? 'Occupied'
            : eventData[stateProperty] === false
              ? 'Vacant'
              : '-';
        }
        if (
          eventType === EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_UPDATE_OCCUPANCY_SENSOR_WET
        ) {
          return eventData[stateProperty] === true
            ? INCONTINENCE_SENSOR_STATES.WET
            : eventData[stateProperty] === false
              ? INCONTINENCE_SENSOR_STATES.DRY
              : '-';
        }
      }
    }
    if (sensor.devicetype === DEVICE_TYPES.RADAR_SENSOR) {
      const stateProperty = DEVICE_EVENTS_PROPERTY[eventType];
      if (eventData && !isNil(eventData[stateProperty])) {
        if (eventType === EVENT_TYPES.RADAR_SENSOR.RADAR_PRESENCE_EVENT) {
          return eventData[stateProperty];
        }
      }
    }
    if (sensor.devicetype === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR) {
      const stateProperty = DEVICE_EVENTS_PROPERTY[eventType];
      if (eventData && !isNil(eventData[stateProperty])) {
        if (
          eventType ===
          EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR
        ) {
          return eventData[stateProperty];
        } else if (eventType === DEVICE_EVENTS_PROPERTY.humidity) {
          return eventData[stateProperty];
        }
      }
    }
    if (sensor.devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR) {
      const isFlushActive = getFlushSensorActive(eventData, eventData?.message_type || eventType);
      if (!isNil(isFlushActive)) {
        return isFlushActive === true
          ? DEVICE_STATES.FLUSH_SENSOR.ACTIVE
          : isFlushActive === false
            ? DEVICE_STATES.FLUSH_SENSOR.INACTIVE
            : '-';
      }
    }
    if (sensor.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR) {
      if (eventType === COOKTOP_ALERTS_TAB) {
        if (!eventData) return '-';
        return eventData.iseventdetected === true
          ? DEVICE_STATES.COOKTOP_SENSOR.ACTIVE
          : DEVICE_STATES.COOKTOP_SENSOR.INACTIVE;
      }
      const stateProperty = DEVICE_EVENTS_PROPERTY[eventType];
      if (eventData && !isNil(eventData[stateProperty])) {
        if (
          eventType === EVENT_TYPES.COOKTOP_SENSOR.PRESENCE ||
          eventType === EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING
        ) {
          const isDetected =
            eventData[stateProperty] === true ||
            (eventData[stateProperty] || '').toString().toLowerCase() === 'detected';
          return isDetected
            ? DEVICE_STATES.COOKTOP_SENSOR.ACTIVE
            : DEVICE_STATES.COOKTOP_SENSOR.INACTIVE;
        }
        if (COOKTOP_EVENT_TYPES.includes(eventType)) {
          const isDetected =
            eventData[stateProperty] === true ||
            (eventData[stateProperty] || '').toString().toLowerCase() === 'detected';
          return isDetected
            ? DEVICE_STATES.COOKTOP_SENSOR.ACTIVE
            : DEVICE_STATES.COOKTOP_SENSOR.INACTIVE;
        }
      }
    }
    if (sensor.devicetype === DEVICE_TYPES.ENVIRONMENTAL_SENSOR) {
      const messageType = eventData['message_type'];
      const stateProperty = DEVICE_EVENTS_PROPERTY[messageType];
      const observations = eventData[stateProperty];
      const data = getLastObservation(observations);
      return data;
    }
    return '-';
  };

  return (
    <div>
      <div
        ref={historyContainerRef}
        className={`history-events-container ${eventType}`}
        onScroll={handleHistoryScroll}
      >
        {historyEvents.slice(0, visibleEventCount).map((event, index) => {
          const sensorState = getSensorState(event, sensorDetails);
          const dataTempUnit = getDataTempUnit(event);
          const isFallDetectedRow =
            sensorDetails?.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR &&
            sensorState === RADAR_PRESENCE_TYPES.FALL_DETECTED;
          const isAlertActiveRow =
            sensorDetails?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR &&
            eventType === COOKTOP_ALERTS_TAB &&
            sensorState === DEVICE_STATES.COOKTOP_SENSOR.ACTIVE;
          return (
            <div
              key={index}
              className={`modal-wellness-card-container history-item${isFallDetectedRow ? ' fall-detected-row' : ''}${isAlertActiveRow ? ' cooktop-alert-row' : ''}`}
            >
              <div className="modal-table-data-history-container">
                <div className="modal-wellness-list-item-content">
                  <div className="modal-wellness-item-content-header">
                    <div className="modal-wellness-item-content-day-info">
                      {getDayLabelOrDate(
                        event?.utc_timestamp_inms,
                        getTimeZoneFormat(timezone),
                        loginUserLocale
                      )}
                    </div>
                    <HSText className="card-today-value">
                      {getTimeFormat(
                        event?.utc_timestamp_inms,
                        getTimeZoneFormat(timezone),
                        loginUserTimeformat?.is24hoursformat
                      )}
                    </HSText>
                  </div>
                  <SensorState
                    data={sensorState}
                    eventType={eventType}
                    userTempUnit={userTempUnit}
                    dataTempUnit={dataTempUnit}
                    trans={trans}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

Table.propTypes = {
  userEvents: PropTypes.array,
  timezone: PropTypes.string,
  userTempUnit: PropTypes.string,
  loginUserLocale: PropTypes.string,
  selectedLanguage: PropTypes.string,
  sensorDetails: PropTypes.object,
  eventType: PropTypes.string,
  loginUserTimeformat: PropTypes.shape({
    is24hoursformat: PropTypes.bool.isRequired,
  }).isRequired,
};

const TableWrapper = styled.div`
  .modal-wellness-item-content {
    height: 83px;
    width: unset;
    gap: 180px;
    &.accessory_state_update_door_window_sensor_open {
      .modal-wellness-item-content-statistic {
        padding-left: 7px;
      }
    }
    &.accessory_state_update_smart_plug_on {
      gap: 168px;
      .modal-wellness-item-content-statistic-info-value {
        position: relative;
        right: 13px;
      }
    }
    &.radar_presence_event {
      gap: 180px;
    }
  }
  .modal-wellness-item-content-statistic-info-value {
    align-items: center;
    display: flex;
    &.open {
      right: 15px;
    }
  }
  svg {
    height: 24px;
  }
  .history-events-container {
    height: 503px;
    overflow: auto;
    background: #f5f5f5;
    border-radius: 16px;
    &.accessory_state_update_smart_plug_on {
      .modal-wellness-item-content-statistic-info-value {
        position: relative;
        right: 13px;
      }
    }
    &.accessory_state_update_occupancy_sensor_wet {
      .sensor-state {
        height: 35px;
        width: 46px;
        path {
          stroke: none !important;
        }
      }
    }
  }
  .history-item {
    padding: 15px 0;
    .modal-wellness-item-content-header {
      position: relative;
      left: 11px;
      align-items: center;
      gap: 61px;
    }
    .modal-wellness-item-content-statistic {
      position: relative;
      left: -3px;
      &.open {
        left: -9px;
      }
    }
    .modal-wellness-list-item-content {
      gap: 198px;
    }
  }
  .sensor-state {
    height: 24px;
    position: relative;
    right: 7px;
  }
  .sensor-state.active,
  .sensor-state.inactive,
  .sensor-state-title.active,
  .sensor-state-title.inactive {
    color: #005d98;
  }
  .modal-wellness-card-container {
    border-bottom: 1px solid #c9c9c952;
  }
  .fall-detected-row {
    background-color: rgba(255, 77, 79, 0.08);
    border-left: 3px solid #ff4d4f;
  }
  .cooktop-alert-row {
    background-color: rgba(250, 173, 20, 0.1);
    border-left: 3px solid #faad14;
  }
  .sensor-state-title.alert-active {
    color: #d46b08;
    font-weight: 600;
  }
  .modal-wellness-list-item-content {
    background: transparent;
  }
  .card-today-value {
    position: relative;
    left: 10px;
  }
  .latest-item {
    &.accessory_state_lan_smart_plug {
      .modal-wellness-item-content-statistic-info-value {
        right: 0px;
      }
    }
  }
`;

export const SensorTable = ({
  timezone,
  userTempUnit,
  loginUserLocale,
  loginUserTimeformat,
  selectedLanguage,
  sensorDetails,
  groupedUserEvents,
}) => {
  return (
    <TableWrapper className="tab-content">
      <div className="tabs-container sensor-table-tabs">
        {groupedUserEvents.length > 0 ? (
          <Tabs
            defaultActiveKey="1"
            className="env-tabs"
            tabPosition="top"
            items={groupedUserEvents.map((events) => {
              return {
                label: (
                  <HSText className="tab-title">{SENSOR_STATE_TITLE_MAPPING[events.type]}</HSText>
                ),
                key: events.type,
                children: (
                  <Table
                    userEvents={events.events}
                    timezone={timezone}
                    userTempUnit={userTempUnit}
                    loginUserLocale={loginUserLocale}
                    selectedLanguage={selectedLanguage}
                    sensorDetails={sensorDetails}
                    eventType={events.type}
                    loginUserTimeformat={loginUserTimeformat}
                  />
                ),
              };
            })}
          />
        ) : (
          <HSText className="no-events">No Data Available</HSText>
        )}
      </div>
    </TableWrapper>
  );
};

SensorTable.propTypes = {
  timezone: PropTypes.string,
  loginUserTimeformat: PropTypes.shape({
    is24hoursformat: PropTypes.bool.isRequired,
  }).isRequired,
  loginUserLocale: PropTypes.string,
  userTempUnit: PropTypes.string,
  selectedLanguage: PropTypes.string,
  sensorDetails: PropTypes.object,
  groupedUserEvents: PropTypes.array,
};
